import { QueryClient } from '@tanstack/react-query'
import { EventLinkedPlansResponse } from 'app/frontend/base/api/event'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'

const visualIdsInRaidPlan = (raidPlan: RaidPlan) => {
  const abilities = raidPlan.timeline.encounter_assignments?.abilities
  if (!abilities) return []
  return Object.values(abilities).flatMap((ability) =>
    typeof ability.visual_id === 'number' ? [ability.visual_id] : [],
  )
}

const eventLinkedPlanQueries = (queryClient: QueryClient, teamId: number) =>
  queryClient
    .getQueriesData<EventLinkedPlansResponse>({
      queryKey: ['eventLinkedPlans'],
    })
    .filter(([queryKey, previous]) => {
      const scope = queryKey[1] as { teamId?: number } | undefined
      return !!previous && scope?.teamId === teamId
    })

export const applyLinkedRaidPlanUpdate = (
  queryClient: QueryClient,
  teamId: number,
  nextRaidPlan: RaidPlan,
) => {
  queryClient.setQueriesData<RaidPlan[] | undefined>(
    { queryKey: ['raidPlans'] },
    (previous) => {
      if (!previous) return previous
      let hasMatch = false
      const next = previous.map((raidPlan) => {
        if (raidPlan.id !== nextRaidPlan.id) return raidPlan
        hasMatch = true
        return nextRaidPlan
      })
      return hasMatch ? next : previous
    },
  )
  queryClient.setQueryData(
    [
      'raidPlan',
      {
        owner: { ownerType: 'team', ownerId: teamId },
        planId: nextRaidPlan.id,
      },
    ],
    nextRaidPlan,
  )

  const referencedVisualIds = visualIdsInRaidPlan(nextRaidPlan)
  eventLinkedPlanQueries(queryClient, teamId).forEach(
    ([queryKey, previous]) => {
      const previousRaidPlan = previous?.raidPlans.find(
        (plan) => plan.id === nextRaidPlan.id,
      )
      if (!previous || !previousRaidPlan) return
      // Only newly added references can be fetched; existing ones the server
      // omitted (deleted visuals) would otherwise refetch on every save.
      const previousVisualIds = new Set(visualIdsInRaidPlan(previousRaidPlan))
      const missingVisual = referencedVisualIds.some(
        (visualId) =>
          !previousVisualIds.has(visualId) &&
          !previous.visualPlans.some((visual) => visual.id === visualId),
      )
      if (missingVisual) {
        void queryClient.invalidateQueries({ queryKey })
        return
      }
      queryClient.setQueryData<EventLinkedPlansResponse>(queryKey, {
        ...previous,
        raidPlans: previous.raidPlans.map((plan) =>
          plan.id === nextRaidPlan.id ? nextRaidPlan : plan,
        ),
      })
    },
  )
}

const replaceVisual = (visuals: VisualPlan[], nextVisual: VisualPlan) => {
  let hasMatch = false
  const next = visuals.map((visual) => {
    if (visual.id !== nextVisual.id) return visual
    hasMatch = true
    return nextVisual
  })
  return hasMatch ? next : visuals
}

export const applyLinkedVisualPlanUpdate = (
  queryClient: QueryClient,
  teamId: number,
  nextVisual: VisualPlan,
) => {
  eventLinkedPlanQueries(queryClient, teamId).forEach(
    ([queryKey, previous]) => {
      if (
        !previous?.visualPlans.some((visual) => visual.id === nextVisual.id)
      ) {
        return
      }
      queryClient.setQueryData<EventLinkedPlansResponse>(queryKey, {
        ...previous,
        visualPlans: replaceVisual(previous.visualPlans, nextVisual),
      })
    },
  )
  queryClient.setQueriesData<VisualPlan[] | undefined>(
    { queryKey: ['visualPlans'] },
    (previous) => {
      if (!previous) return previous
      const next = replaceVisual(previous, nextVisual)
      return next === previous ? previous : next
    },
  )
  queryClient.setQueriesData<VisualPlan | undefined>(
    { queryKey: ['visualPlan'] },
    (previous) => {
      if (!previous || previous.id !== nextVisual.id) return previous
      return nextVisual
    },
  )
}

export const applyLinkedVisualPlanDelete = (
  queryClient: QueryClient,
  teamId: number,
  visualPlanId: number,
) => {
  eventLinkedPlanQueries(queryClient, teamId).forEach(
    ([queryKey, previous]) => {
      if (!previous?.visualPlans.some((visual) => visual.id === visualPlanId)) {
        return
      }
      queryClient.setQueryData<EventLinkedPlansResponse>(queryKey, {
        ...previous,
        visualPlans: previous.visualPlans.filter(
          (visual) => visual.id !== visualPlanId,
        ),
      })
    },
  )
  queryClient.setQueriesData<VisualPlan[] | undefined>(
    { queryKey: ['visualPlans'] },
    (previous) => {
      if (!previous) return previous
      const next = previous.filter((visual) => visual.id !== visualPlanId)
      return next.length === previous.length ? previous : next
    },
  )
  queryClient
    .getQueriesData<VisualPlan>({ queryKey: ['visualPlan'] })
    .forEach(([queryKey, data]) => {
      if (data?.id !== visualPlanId) return
      queryClient.removeQueries({ queryKey, exact: true })
    })
}
